import type {SmsState} from 'src/types/sms';
import type {NodeSchedule} from 'src/types/node-schedule';
import type {
  NoteWriteback,
  MessagingNoteWriteback,
} from 'src/types/note-writeback';
import type {Dispatch, ThunkAction} from 'src/reducers';
import * as reduxApiBff from 'src/utils/redux-api-bff';
import * as reduxApi from 'src/utils/redux-api-v2';
import {parseSms} from 'src/utils/sms';
import type {FilterRule} from 'src/types/filter';


export const RECEIVE_SMS_INFO = 'sms/receive';
export const UPDATE_SMS_INFO = 'sms/update';
export const UPDATE_NODE_SCHEDULE = 'sms/update-node-schedule';
export const UPDATE_NOTE_WRITEBACK = 'sms/updateNoteWriteback';

export const RESET = 'sms/reset';

export const ADD_ALERT = 'sms/alerts/add';
export const REMOVE_ALERT = 'sms/alerts/remove';
export const SET_ALERT_TO = 'sms/alerts/set-to';
export const SET_ALERT_RULES = 'sms/alerts/set-rules';

type UpdateSmsInfoPayload = Partial<SmsState>;

type ReceiveSmsInfoAction = {
  type: typeof RECEIVE_SMS_INFO;
  payload: SmsState;
};

type UpdateSmsInfoAction = {
  type: typeof UPDATE_SMS_INFO;
  payload: UpdateSmsInfoPayload;
};

type ResetStateAction = {
  type: typeof RESET;
};

type UpdateNodeScheduleAction = {
  type: typeof UPDATE_NODE_SCHEDULE;
  payload: Partial<NodeSchedule>;
};

type UpdateNoteWritebackAction = {
  type: typeof UPDATE_NOTE_WRITEBACK;
  payload: Partial<MessagingNoteWriteback>;
};

type AddAlertAction = {
  type: typeof ADD_ALERT;
  payload: {
    rules: Array<FilterRule>;
  };
};

type RemoveAlertAction = {
  type: typeof REMOVE_ALERT;
  payload: {
    id: string;
  };
};

type SetAlertToAction = {
  type: typeof SET_ALERT_TO;
  payload: {
    id: string;
    to: string | null;
  };
};

type SetAlertRulesAction = {
  type: typeof SET_ALERT_RULES;
  payload: {
    id: string;
    blockId: string;
    rules: Array<FilterRule>;
  };
};

export type SmsAction =
  | ReceiveSmsInfoAction
  | UpdateSmsInfoAction
  | UpdateNodeScheduleAction
  | ResetStateAction
  | UpdateNoteWritebackAction
  | AddAlertAction
  | RemoveAlertAction
  | SetAlertToAction
  | SetAlertRulesAction;

export const reset = (): ResetStateAction => ({type: RESET});

export const receiveSmsNode = (payload: SmsState): ReceiveSmsInfoAction => ({
  type: RECEIVE_SMS_INFO,
  payload,
});

export const updateSmsInfo = (
  payload: UpdateSmsInfoPayload,
): UpdateSmsInfoAction => ({
  type: UPDATE_SMS_INFO,
  payload,
});

export const getSmsTaskNode: (id: string) => ThunkAction<unknown> =
  (id) => async (dispatch: Dispatch) => {
    const fetchedNodeResponse = await dispatch(
      reduxApiBff.get(`node/composite`, {
        node_id: id,
      }),
    );
    const parsedSmsNode = parseSms(fetchedNodeResponse.body);
    return dispatch(updateSmsInfo(parsedSmsNode));
  };

export const getProvisionedPhoneNumbers: (
  id: string | number,
) => ThunkAction<unknown> = (id) => (dispatch: Dispatch) => {
  return dispatch(
    reduxApi.get(`provisioned-phone`, {
      associated_with_category: String(true),
      category_id: String(id),
    }),
  );
};

export const updateNodeSchedule = (
  payload: Partial<NodeSchedule>,
): UpdateNodeScheduleAction => ({
  type: UPDATE_NODE_SCHEDULE,
  payload,
});

export const updateNoteWriteback = (
  payload: Partial<MessagingNoteWriteback>,
): UpdateNoteWritebackAction => ({
  type: UPDATE_NOTE_WRITEBACK,
  payload,
});

export const addAlert = (rules: Array<FilterRule>): AddAlertAction => ({
  type: ADD_ALERT,
  payload: {rules},
});

export const removeAlert = (id: string): RemoveAlertAction => ({
  type: REMOVE_ALERT,
  payload: {id},
});

export const setAlertTo = (
  id: string,
  to: string | null,
): SetAlertToAction => ({
  type: SET_ALERT_TO,
  payload: {id, to},
});

export const setAlertRules = (
  id: string,
  blockId: string,
  rules: Array<FilterRule>,
): SetAlertRulesAction => ({
  type: SET_ALERT_RULES,
  payload: {id, blockId, rules},
});
